import { type RefObject, useLayoutEffect, useState } from "react";
import {
  DAY_COLUMN_PX,
  MAX_WINDOW_DAYS,
  MIN_WINDOW_DAYS,
  NAME_COLUMN_PX,
} from "./attendance.config";

// How many day columns fit inside `ref` (minus the name column), clamped to [MIN, MAX], recomputed on
// resize. Starts at MAX so the first paint matches the fetched window; useLayoutEffect adjusts before paint.
//
// `ref` MUST be width-constrained to the viewport (the app shell puts `min-w-0` on the flex column holding
// <main>). Otherwise the grid's width blows the flex item out to its content size, `clientWidth` tracks the
// grid instead of the available space, and the ResizeObserver feeds that back — a loop that ratchets the
// columns down on load.
export function useVisibleDayCount(ref: RefObject<HTMLElement | null>): number {
  const [count, setCount] = useState(MAX_WINDOW_DAYS);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const fit = Math.floor((el.clientWidth - NAME_COLUMN_PX) / DAY_COLUMN_PX);
      setCount(Math.max(MIN_WINDOW_DAYS, Math.min(MAX_WINDOW_DAYS, fit)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return count;
}
